import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineCheckCircle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "./Statuses.css";

const Statuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to show page immediately
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({
    statusType: "",
    statusName: "",
    isFinal: false,
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      // Load data in background without blocking UI
      // Use setTimeout to ensure page renders first
      const timer = setTimeout(() => {
        loadStatuses(false);
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setError("Please login to view statuses");
      setLoading(false);
    }
  }, [tenantId]);

  const loadStatuses = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");
      const data = await masterDataService.getStatuses(tenantId);
      setStatuses(data || []);
    } catch (err) {
      setError("Failed to load statuses. Please try again.");
      console.error("Error loading statuses:", err);
      setStatuses([]);
    } finally {
      setLoading(false); // Always set loading to false after API call completes
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredStatuses = statuses.filter(
    (status) =>
      status.statusName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      status.statusType?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingStatus(null);
    setFormData({
      statusType: "",
      statusName: "",
      isFinal: false,
      status: "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormData({
      statusType: status.statusType || "",
      statusName: status.statusName || "",
      isFinal: status.isFinal || false,
      status: status.status || "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deleteStatus(id);
      await loadStatuses(false); // Don't show loading on refresh after delete
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete status. Please try again.");
      console.error("Error deleting status:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.statusName.trim()) {
      errors.statusName = "Status name is required";
    } else if (formData.statusName.length > 50) {
      errors.statusName = "Status name must be less than 50 characters";
    }
    if (formData.statusType && formData.statusType.length > 50) {
      errors.statusType = "Status type must be less than 50 characters";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const statusData = {
        tenantId: tenantId,
        statusType: formData.statusType.trim() || null,
        statusName: formData.statusName.trim(),
        isFinal: formData.isFinal,
        status: formData.status,
      };

      if (editingStatus) {
        statusData.statusId = editingStatus.statusId;
        await masterDataService.updateStatus(
          editingStatus.statusId,
          statusData,
        );
      } else {
        await masterDataService.createStatus(statusData);
      }

      await loadStatuses(false); // Don't show loading on refresh after save
      setShowModal(false);
      setFormData({
        statusType: "",
        statusName: "",
        isFinal: false,
        status: "Active",
      });
      setEditingStatus(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save status. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStatus(null);
    setFormData({
      statusType: "",
      statusName: "",
      isFinal: false,
      status: "Active",
    });
    setFormErrors({});
  };

  return (
    <div className="statuses-page pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h2>Statuses</h2>
          <p>Manage status types for workflow tracking</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search statuses..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Status
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="table-info">
            {loading && statuses.length === 0 ? (
              <span style={{ color: "var(--text-secondary)" }}>Loading...</span>
            ) : (
              `${filteredStatuses.length} ${filteredStatuses.length === 1 ? "status" : "statuses"}`
            )}
          </div>
        </div>

        {loading && statuses.length === 0 ? (
          <div className="table-loading">
            <PageLoading variant="compact" message="Loading statuses…" />
          </div>
        ) : filteredStatuses.length === 0 ? (
          <div className="empty-state">
            <HiOutlineCheckCircle className="empty-icon" />
            <h3>No statuses found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first status"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                New Status
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status Type</th>
                <th>Status Name</th>
                <th>Is Final</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStatuses.map((status) => (
                <tr key={status.statusId}>
                  <td>{status.statusId}</td>
                  <td>{status.statusType || "-"}</td>
                  <td className="name-cell">{status.statusName}</td>
                  <td>
                    {status.isFinal ? (
                      <span className="status-badge active">Yes</span>
                    ) : (
                      <span className="status-badge inactive">No</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${status.status?.toLowerCase()}`}
                    >
                      {status.status || "Active"}
                    </span>
                  </td>
                  <td>
                    {status.createdAt
                      ? new Date(status.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(status)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(status)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingStatus ? "Edit Status" : "New Status"}
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="status-form"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingStatus
                  ? "Updating..."
                  : "Creating..."
                : editingStatus
                  ? "Update"
                  : "Create"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="status-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="statusType">Status Type</label>
              <input
                type="text"
                id="statusType"
                value={formData.statusType}
                onChange={(e) =>
                  setFormData({ ...formData, statusType: e.target.value })
                }
                className={formErrors.statusType ? "error" : ""}
                placeholder="e.g., Kaizen, RCA, OPL"
                maxLength={50}
              />
              {formErrors.statusType && (
                <span className="field-error">{formErrors.statusType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="statusName">
                Status Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="statusName"
                value={formData.statusName}
                onChange={(e) =>
                  setFormData({ ...formData, statusName: e.target.value })
                }
                className={formErrors.statusName ? "error" : ""}
                placeholder="e.g., Pending, Approved, Rejected"
                maxLength={50}
              />
              {formErrors.statusName && (
                <span className="field-error">{formErrors.statusName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="isFinal" className="checkbox-label">
                <input
                  type="checkbox"
                  id="isFinal"
                  checked={formData.isFinal}
                  onChange={(e) =>
                    setFormData({ ...formData, isFinal: e.target.checked })
                  }
                />
                <span>Is Final Status</span>
              </label>
              <small className="field-hint">
                Final statuses cannot be changed to other statuses
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => handleDelete(deleteConfirm.statusId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.statusName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The status will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Statuses;
